<template>
  <BaseContainer class="jps-contact">
    <div class="jps-contact jps-static" v-if="token" ref="root">
      <header class="is-white">
        <h1 class="title">
          <BaseIcon
            name="koicon/koicon-34"
            class="jps-koicon jps-red"
            size="size-full"
          />
          <span class="is-size-3-desktop is-size-4-touch">
            {{ ls({ ja: 'お問い合わせ', en: 'Contact us' },) }}
          </span>
        </h1>
      </header>
      <div class="jps-static-main">
        <div class="jps-static-content">
          <template v-if="!contactSent">
            <section class="content">
              <h2 class="title is-4">{{ ls({ ja: 'お問い合わせの前に', en: 'Prior to Contact' }) }}</h2>
              <ul>
                <li v-html="ls(localLangResource.mContact1)"></li>
                <li v-html="ls(localLangResource.mContact2)"></li>
                <li v-html="ls(localLangResource.mContact3)"></li>
                <li v-html="ls(localLangResource.mContact4)"></li>
              </ul>
            </section>
            <section>
              <form @submit.prevent="send()">
                <h2 class="title is-4">{{ ls({ ja: "お問い合わせフォーム", en: "Contact Form" }) }}</h2>
                <ValidationForm
                  :rule="rule"
                  :target="contact"
                  only="type"
                />
                <p
                  class="jps-message jps-blue"
                  v-show="contact.type === 'DATA_USAGE'"
                >
                  {{ ls({
                    ja: "※具体的な内容には、ご利用希望のデータのタイトルとURLをお書きください",
                    en: "Please specify the title and URL of the content you want to use.",
                  }) }}
                </p>
                <ValidationForm
                  :rule="rule"
                  :target="contact"
                  only="content"
                ></ValidationForm>
                <p class="jps-message jps-red" v-show="_containUrl">
                  {{ ls({
                    ja: "内容にジャパンサーチ以外のURLを含むことはできません",
                    en: "You can not include any URL other than Japan Search in your message.",
                  }) }}
                </p>
                <ValidationForm
                  :rule="rule"
                  :target="contact"
                  only="name,affiliation,email"
                />
                <button
                  class="contact-submit-button jps-button jps-fullwidth"
                  type="submit"
                  :disabled="!isValid"
                >
                  {{ ls({ ja: "送信", en: "Send" }) }}
                </button>
              </form>
            </section>
          </template>
          <template v-else>
            <p class="jps-contact-sent" v-html="ls(localLangResource.mContactSent)"></p>
            <table class="jps-table">
              <tbody>
                <template v-for="ruleField in rule.fields">
                  <tr v-if="contact[ruleField.field] && ruleField.field !== 'id'">
                    <th>{{ ls(ruleField.label) }}</th>
                    <td>
                      {{
                        ruleField.field === 'type'
                            ? led(contactTypeDef, contact[ruleField.field])
                            : contact[ruleField.field]
                      }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>

            <p class="jps-link-top">
              <BaseLink to="/">{{ ls({ ja: 'トップに戻る', en: 'Back to top' }) }}</BaseLink>
            </p>
          </template>
        </div>
      </div>
    </div>
  </BaseContainer>
</template>

<script lang="ts" setup>
definePageMeta({
  disableDefaultMargin: true,
})

const { getPageAndCache } = usePage()
const { page, meta } = await getPageAndCache()
useHead(meta)

const { ls, led } = useI18n()
const { loadingExecute } = useLoader()
const { sendContact } = useContact()
const { snackbarError } = useSnackbar()

const contact: Ref<Contact> = ref({ type: 'DATA_USAGE' })
const rule: ValidationRule = CONTACT_RULE
const contactSent: Ref<boolean> = ref(false)
const contactTypeDef = ref(CONTACT_TYPE_DEF)
const root: Ref<HTMLElement> = ref()

const isValid = computed(() => {
  return contact.value.content && !_containUrl.value && hasEmail.value
})

const hasEmail = computed(() => {
  return contact.value.email && validateEmail(contact.value.email)
})

const _containUrl = computed(() => {
  return containUrl(contact.value.content)
})

const token = computed((): string => {
  const tokens = page.pageParams.token
  if (Array.isArray(tokens)) {
    return tokens[0]
  } else {
    return tokens
  }
})

const send = () => {
  if (isValid.value && !contactSent.value) {
    contact.value.id = token.value
    loadingExecute(sendContact(contact.value))
      .then(() => {
        contactSent.value = true
        if (import.meta.client) {
          scrollToTop()
        }
      })
      .catch(code => {
        snackbarError({
          ja: '送信に失敗しました。もう一度送信ボタンを押してください。繰り返してもうまくいかない場合、画面をリロードして再度お試しください（入力内容はリセットされます）。',
          en: 'Failed to send message. Please try again with below button.',
        })
      })
  }
}

const localLangResource = {
  mContact1: {
    ja: 'お問い合わせは以下のフォームから受け付けております。',
    en: 'We will send you an E-mail once we have checked the contents of your inquiry.',
  },
  mContact2: {
    ja: 'お問い合わせの内容によっては回答できない場合や時間を要する場合があること、また、ご要望に沿えない場合もあることを、あらかじめご了承ください。',
    en: 'Please acknowledge in advance that we may not be able to respond or it may take some time for response depending on your inquiry, and we may not be able to accommodate some requests.',
  },
  mContact3: {
    ja: 'ジャパンサーチと連携を希望される方は<a href="/cooperation">「連携をご希望の機関の方へ」</a>のページ及び<a href="/cooperation-faq">「連携に関するQ&A」</a> をご覧のうえ、お問い合わせください。',
    en: 'If your institution wishes to cooperate with Japan Search, please contact us after reviewing the <a href="/cooperation">To Institutions Interested in Cooperating (Japanese only)</a> page and the <a href="/cooperation-faq">Cooperation Q & A (Japanese only)</a>',
  },
  mContact4: {
    ja: '入力していただいた個人情報は、今回の問合せ手続のみに使用し、他の目的には使用しません。当サイトにおける個人情報の取扱いについては<a href="/policy/privacy">「プライバシーポリシー 」</a>をご覧ください。',
    en: 'The personal information you input will be used solely for this inquiry procedure and will not be used for any other purposes. Please see the <a href="/policy/privacy">Privacy Policy</a> on how personal information is handled on this site.',
  },
  mContactSent: {
    ja: 'お問い合わせいただきありがとうございました。以下の内容で受け取りました。<br>フィードバックについては、改善に向けて検討させていただきます。また、全てのお問い合わせにお返事できませんこと、ご了承ください。',
    en: 'Thank you for contacting us. Your messages is accepted as follows: ',
  },
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-contact {
  .jps-validation-form {
    margin-top: 1rem;
  }
  .jps-static-main {
    .jps-static-content {
      form {
        background: $jps-gray-100;
        padding: 1rem;
        border-radius: 16px;
        .contact-submit-button {
          margin-top: 1rem;
        }
      }
      textarea {
        height: 4.5rem;
      }
      .field button {
        margin-top: 2rem;
        width: 6rem;
      }
      .table {
        margin-top: 2rem;
        th {
          width: 20%;
        }
      }
      .jps-link-top {
        text-align: center;
      }
    }
  }
}
.jps-koicon {
  @include is-mobile {
    max-width: 1.5rem;
    max-height: 1.5rem;
  }
  @include is-tablet {
    max-width: 2rem;
    max-height: 2rem;
  }
}
</style>
